/**
 * @file assets/description-sanitizer.js
 * @description Product-card description pipeline: parses the escaped `data-description-html`
 *   attribute on `<template data-description-preview-source data-description-rich-source>` into
 *   a detached template, rebuilds it through the allowlist HTML/SVG sanitizer (docs/DECISIONS.md
 *   D-005, D-017, D-043; `h1` rebuilt as `h2`, `title` kept only inside `svg`, no `class` on any
 *   element) once per template, swaps the result into `.product-card__description-full-text`
 *   and builds the text-only word-limited previews from the same sanitized fragment (words joined
 *   where the source has whitespace or a block edge, so inline markup inside a word adds no
 *   space). Loaded by layout/theme.liquid after theme-dom.js and before product-card.js, which
 *   calls window.DescriptionSanitizer.initRichDescriptionFullText(root) and
 *   .initDescriptionPreviews(root) for every card root. Depends on window.ThemeDom only.
 * @see docs/ARCHITECTURE.md §4 "Description contract"
 */
(() => {
  const { normalizeWhitespace } = window.ThemeDom;

  const DESCRIPTION_PREVIEW_INLINE_TAGS = new Set([
    'a',
    'abbr',
    'b',
    'cite',
    'code',
    'em',
    'i',
    'mark',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'u'
  ]);
  // Elements whose edges separate the words around them, as a line break does on the page, so two
  // paragraphs or list items preview as two sentences. Inline markup is no boundary: `ab<b>cd</b>`
  // previews as one word (C-12).
  const DESCRIPTION_PREVIEW_BLOCK_TAGS = new Set([
    'address',
    'blockquote',
    'dd',
    'div',
    'dl',
    'dt',
    'figcaption',
    'figure',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'li',
    'ol',
    'p',
    'pre',
    'table',
    'td',
    'th',
    'tr',
    'ul'
  ]);
  const DESCRIPTION_PREVIEW_SKIP_TAGS = new Set([
    'audio',
    'button',
    'canvas',
    'iframe',
    'img',
    'input',
    'noscript',
    'picture',
    'script',
    'select',
    'style',
    'svg',
    'textarea',
    'video'
  ]);
  const DESCRIPTION_RICH_ALLOWED_TAGS = new Set([
    'a',
    'b',
    'blockquote',
    'br',
    'circle',
    'code',
    'defs',
    'desc',
    'div',
    'em',
    'ellipse',
    'g',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'i',
    'img',
    'image',
    'line',
    'li',
    'ol',
    'path',
    'p',
    'polygon',
    'polyline',
    'pre',
    'rect',
    'small',
    'span',
    'strong',
    'svg',
    'sub',
    'sup',
    'title',
    'tspan',
    'text',
    'u',
    'use',
    'ul'
  ]);
  const DESCRIPTION_RICH_SKIP_TAGS = new Set([
    'audio',
    'button',
    'canvas',
    'form',
    'iframe',
    'input',
    'noscript',
    'object',
    'picture',
    'script',
    'select',
    'source',
    'style',
    'textarea',
    'video'
  ]);
  // Allowed tags that are rebuilt under another name, applied where the output element is created
  // (attributes and children are kept). The page already has one <h1>, so a description heading
  // starts at h2. `title` is allowed only inside a rebuilt <svg> (an SVG title); an HTML <title> in
  // a description is dropped with its text, see sanitizeRichDescriptionNode.
  const DESCRIPTION_RICH_TAG_REMAP = new Map([['h1', 'h2']]);
  // `id` and `style` are deliberately absent for HTML elements: ids would collide with theme ids and
  // clobber window properties, and inline styles are a CSS injection surface. SVG keeps both (below)
  // because <use>, gradients, and clip paths need ids, and SVG elements are not window-named.
  // `class` is absent from all three sets (L-58, C-7): a description class such as
  // `collection-projects` or `product-card__price` would match theme selectors inside the card
  // (index-layout.js's column test, product-card.js's price writer), and no theme rule styles
  // description content by class (product-shared.css styles `.product-rich-description` by tag; `<style>`
  // is stripped, so an SVG class had nothing to select it either).
  const DESCRIPTION_RICH_ALLOWED_GLOBAL_ATTRIBUTES = new Set([
    'aria-hidden',
    'aria-label',
    'height',
    'role',
    'width'
  ]);
  const DESCRIPTION_RICH_ALLOWED_IMAGE_ATTRIBUTES = new Set([
    'alt',
    'aria-hidden',
    'aria-label',
    'decoding',
    'height',
    'loading',
    'role',
    'sizes',
    'src',
    'srcset',
    'title',
    'width'
  ]);
  const DESCRIPTION_RICH_ALLOWED_SVG_ATTRIBUTES = new Set([
    'alignment-baseline',
    'aria-hidden',
    'aria-label',
    'clip-path',
    'clip-rule',
    'cx',
    'cy',
    'd',
    'dominant-baseline',
    'fill',
    'fill-opacity',
    'fill-rule',
    'focusable',
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'height',
    'href',
    'id',
    'marker-end',
    'marker-mid',
    'marker-start',
    'mask',
    'opacity',
    'points',
    'preserveaspectratio',
    'r',
    'role',
    'rx',
    'ry',
    'stroke',
    'stroke-dasharray',
    'stroke-dashoffset',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-miterlimit',
    'stroke-opacity',
    'stroke-width',
    'style',
    'text-anchor',
    'transform',
    'viewbox',
    'width',
    'x',
    'x1',
    'x2',
    'xlink:href',
    'y',
    'y1',
    'y2'
  ]);
  const DESCRIPTION_RICH_SVG_TAGS = new Set([
    'circle',
    'defs',
    'desc',
    'ellipse',
    'g',
    'image',
    'line',
    'path',
    'polygon',
    'polyline',
    'rect',
    'svg',
    'title',
    'tspan',
    'text',
    'use'
  ]);
  const DESCRIPTION_RICH_URL_ATTRIBUTES = new Set(['href', 'xlink:href']);
  const DESCRIPTION_LITERAL_HTML_PATTERN = /<\/?[a-z][\s\S]*>/i;
  // Protocol-relative URLs are rejected unless they point at the Shopify CDN, which older rich-text
  // content uses for uploaded images.
  const DESCRIPTION_TRUSTED_PROTOCOL_RELATIVE_PREFIX = '//cdn.shopify.com/';
  // Inline SVG `style` may only carry plain declarations; url() is limited to same-document fragments.
  const DESCRIPTION_UNSAFE_STYLE_PATTERN = /expression\s*\(|javascript:|@import|behavior\s*:|-moz-binding|url\s*\(\s*['"]?\s*(?!#)/i;
  // Nor may it take an <svg> out of the card: `position: fixed` or `sticky` inside a description link
  // is a click target over the whole page. The position is read back through the browser's CSS parser,
  // because a comment inside a string or url() can hide a declaration from a text pattern, and a
  // substituted value (var(), env(), attr()) is refused since it can resolve to either keyword.
  const DESCRIPTION_UNSAFE_STYLE_POSITION_PATTERN = /fixed|sticky|\(/i;
  // An element of an inert document: parsing a style value on it loads and renders nothing.
  const descriptionStyleProbe = document.implementation.createHTMLDocument('').createElement('div');
  const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
  const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';
  // One sanitized fragment per <template>, for the lifetime of the page.
  const sanitizedRichDescriptionFragments = new WeakMap();

  // ---------------------------------------------------------------------------
  // Preview builder: text-only, word-limited copy of the sanitized fragment.
  // ---------------------------------------------------------------------------

  function getDescriptionPreviewWordLimit(previewText, outputMode) {
    if (!previewText || outputMode === 'full') {
      return Infinity;
    }

    const limitAttribute = outputMode === 'mobile'
      ? previewText.dataset.descriptionPreviewMobileWords
      : previewText.dataset.descriptionPreviewDesktopWords;
    const parsedLimit = Number.parseInt(limitAttribute, 10);

    return Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : Infinity;
  }

  /**
   * A word boundary before the next word: whitespace in the source, a block edge, a <br> or skipped
   * media. Nothing is written until a word follows, so the preview never ends in a space.
   */
  function markDescriptionPreviewBoundary(state) {
    if (state.hasText) {
      state.needsSpace = true;
    }
  }

  /**
   * Appends the words of one text node. A run that starts with no whitespace and no boundary before it
   * continues the previous word (`Hello <em>world</em>s`), takes no space and does not count again
   * toward the limit, so the limit is reached only when a further word would start.
   */
  function appendDescriptionPreviewWords(text, targetParent, state) {
    const sourceText = String(text || '');
    const words = normalizeWhitespace(sourceText).split(' ').filter(Boolean);

    if (/^\s/.test(sourceText)) {
      markDescriptionPreviewBoundary(state);
    }

    if (!words.length) {
      return;
    }

    for (let index = 0; index < words.length; index += 1) {
      const continuesWord = index === 0 && state.hasText && !state.needsSpace;

      if (!continuesWord) {
        if (state.remainingWords <= 0) {
          state.isComplete = true;
          return;
        }

        if (state.remainingWords !== Infinity) {
          state.remainingWords -= 1;
        }
      }

      const prefix = state.hasText && !continuesWord ? ' ' : '';
      targetParent.appendChild(document.createTextNode(prefix + words[index]));
      state.hasText = true;
      state.needsSpace = false;
    }

    if (/\s$/.test(sourceText)) {
      markDescriptionPreviewBoundary(state);
    }
  }

  function appendDescriptionPreviewElement(sourceElement, targetParent, state) {
    const tagName = sourceElement.tagName ? sourceElement.tagName.toLowerCase() : '';

    if (!tagName || state.isComplete) {
      return;
    }

    // A <br>, a picture or a drawing adds no words but stands between the words around it.
    if (tagName === 'br' || DESCRIPTION_PREVIEW_SKIP_TAGS.has(tagName)) {
      markDescriptionPreviewBoundary(state);
      return;
    }

    const isBlockElement = DESCRIPTION_PREVIEW_BLOCK_TAGS.has(tagName);
    const shouldPreserveElement = DESCRIPTION_PREVIEW_INLINE_TAGS.has(tagName);
    const nextParent = shouldPreserveElement ? sourceElement.cloneNode(false) : targetParent;

    if (isBlockElement) {
      markDescriptionPreviewBoundary(state);
    }

    appendDescriptionPreviewNodes(sourceElement, nextParent, state);

    if (shouldPreserveElement && nextParent.textContent.trim()) {
      targetParent.appendChild(nextParent);
    }

    if (isBlockElement) {
      markDescriptionPreviewBoundary(state);
    }
  }

  function appendDescriptionPreviewNodes(sourceParent, targetParent, state) {
    const childNodes = Array.from(sourceParent.childNodes || []);

    for (let index = 0; index < childNodes.length; index += 1) {
      if (state.isComplete) {
        return;
      }

      const childNode = childNodes[index];

      if (childNode.nodeType === Node.TEXT_NODE) {
        appendDescriptionPreviewWords(childNode.textContent, targetParent, state);
      } else if (childNode.nodeType === Node.ELEMENT_NODE) {
        appendDescriptionPreviewElement(childNode, targetParent, state);
      }
    }
  }

  function buildDescriptionPreviewFragment(sourceFragment, wordLimit) {
    const fragment = document.createDocumentFragment();
    const state = {
      hasText: false,
      needsSpace: false,
      isComplete: false,
      remainingWords: Number.isFinite(wordLimit) ? wordLimit : Infinity
    };

    appendDescriptionPreviewNodes(sourceFragment, fragment, state);

    return fragment;
  }

  // ---------------------------------------------------------------------------
  // Source parsing.
  // ---------------------------------------------------------------------------

  /**
   * filtered-collection.liquid ships the raw description as an escaped `data-description-html`
   * attribute; parsing it into a detached <template> keeps it inert (no script execution, no
   * resource loads) until sanitized.
   */
  function parseDescriptionSourceFragment(sourceTemplate) {
    const encodedHtml = sourceTemplate ? sourceTemplate.getAttribute('data-description-html') : null;

    if (encodedHtml === null) {
      return document.createDocumentFragment();
    }

    const parserTemplate = document.createElement('template');
    parserTemplate.innerHTML = encodedHtml;
    return parserTemplate.content;
  }

  /** Descriptions saved as literal HTML text (escaped tags) are parsed a second time. */
  function parseLiteralRichDescriptionFragment(sourceFragment) {
    if (!sourceFragment) {
      return document.createDocumentFragment();
    }

    const childNodes = Array.from(sourceFragment.childNodes || []);
    const hasElementChildren = childNodes.some((childNode) => childNode.nodeType === Node.ELEMENT_NODE);
    const sourceText = sourceFragment.textContent ? sourceFragment.textContent.trim() : '';

    if (hasElementChildren || !DESCRIPTION_LITERAL_HTML_PATTERN.test(sourceText)) {
      return sourceFragment;
    }

    const parserTemplate = document.createElement('template');
    parserTemplate.innerHTML = sourceText;

    return parserTemplate.content;
  }

  // ---------------------------------------------------------------------------
  // Sanitizer (D-005, D-017).
  // ---------------------------------------------------------------------------

  /**
   * Shared URL allowlist for hrefs, image sources, and SVG references. Accepts same-origin paths,
   * http(s), and optionally fragments, mailto:/tel:, and data:image/. Protocol-relative values
   * (`//` or `/\`) are accepted only for the Shopify CDN.
   */
  function isSafeDescriptionUrl(value, options = {}) {
    if (!value) return false;

    const normalizedValue = String(value).trim().toLowerCase();
    if (!normalizedValue) return false;

    if (normalizedValue.startsWith('#')) {
      return options.allowFragment === true;
    }

    if (normalizedValue.startsWith('//') || normalizedValue.startsWith('/\\')) {
      return normalizedValue.startsWith(DESCRIPTION_TRUSTED_PROTOCOL_RELATIVE_PREFIX);
    }

    if (normalizedValue.startsWith('/')) {
      return true;
    }

    if (normalizedValue.startsWith('http://') || normalizedValue.startsWith('https://')) {
      return true;
    }

    if (options.allowContact === true && (normalizedValue.startsWith('mailto:') || normalizedValue.startsWith('tel:'))) {
      return true;
    }

    if (options.allowDataImage === true && normalizedValue.startsWith('data:image/')) {
      return true;
    }

    return false;
  }

  function isSafeDescriptionLink(href) {
    return isSafeDescriptionUrl(href, { allowFragment: true, allowContact: true });
  }

  function isSvgDescriptionTag(tagName) {
    return Boolean(tagName && DESCRIPTION_RICH_SVG_TAGS.has(tagName));
  }

  function isSafeDescriptionSvgReference(value, tagName) {
    if (tagName === 'use') {
      // <use> may only reference symbols inside the same sanitized document.
      return Boolean(value) && String(value).trim().startsWith('#');
    }

    return isSafeDescriptionUrl(value, { allowFragment: true, allowDataImage: true });
  }

  function isSafeDescriptionImageSource(value) {
    return isSafeDescriptionUrl(value, { allowDataImage: true });
  }

  function isSafeDescriptionImageSrcset(value) {
    const candidates = String(value || '')
      .split(',')
      .map((candidate) => candidate.trim())
      .filter(Boolean);

    if (!candidates.length) return false;

    return candidates.every((candidate) => isSafeDescriptionImageSource(candidate.split(/\s+/)[0]));
  }

  function declaresUnsafeDescriptionPosition(value) {
    descriptionStyleProbe.style.cssText = value;
    const position = descriptionStyleProbe.style.getPropertyValue('position');
    descriptionStyleProbe.style.cssText = '';

    return DESCRIPTION_UNSAFE_STYLE_POSITION_PATTERN.test(position);
  }

  function isSafeDescriptionStyleValue(value) {
    const normalizedValue = String(value || '');
    if (!normalizedValue.trim()) return false;
    if (/[<>&\\]/.test(normalizedValue)) return false;
    if (DESCRIPTION_UNSAFE_STYLE_PATTERN.test(normalizedValue)) return false;

    return !declaresUnsafeDescriptionPosition(normalizedValue);
  }

  function copyRichDescriptionAttributes(sourceNode, targetNode, tagName) {
    if (!sourceNode || !targetNode || !sourceNode.attributes) return;

    const isSvgTag = isSvgDescriptionTag(tagName);

    Array.from(sourceNode.attributes).forEach((attribute) => {
      const attributeName = attribute.name ? attribute.name.toLowerCase() : '';
      const attributeValue = attribute.value || '';

      if (!attributeName || attributeName.startsWith('on')) {
        return;
      }

      if (tagName === 'img') {
        if (!DESCRIPTION_RICH_ALLOWED_IMAGE_ATTRIBUTES.has(attributeName)) {
          return;
        }

        if (attributeName === 'src' && !isSafeDescriptionImageSource(attributeValue)) {
          return;
        }

        if (attributeName === 'srcset' && !isSafeDescriptionImageSrcset(attributeValue)) {
          return;
        }

        targetNode.setAttribute(attribute.name, attributeValue);
        return;
      }

      if (isSvgTag) {
        if (!DESCRIPTION_RICH_ALLOWED_SVG_ATTRIBUTES.has(attributeName)) {
          return;
        }

        if (DESCRIPTION_RICH_URL_ATTRIBUTES.has(attributeName) && !isSafeDescriptionSvgReference(attributeValue, tagName)) {
          return;
        }

        if (attributeName === 'style' && !isSafeDescriptionStyleValue(attributeValue)) {
          return;
        }

        if (attributeName === 'xlink:href') {
          targetNode.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', attributeValue.trim());
          return;
        }

        targetNode.setAttribute(attribute.name, attributeValue);
        return;
      }

      if (DESCRIPTION_RICH_ALLOWED_GLOBAL_ATTRIBUTES.has(attributeName)) {
        targetNode.setAttribute(attribute.name, attributeValue);
      }
    });
  }

  /**
   * Rebuilds one source node under `targetParent`. `insideSvg` is true when a rebuilt <svg> is an
   * ancestor in the output tree (elements are appended to their parent only after their children are
   * rebuilt, so the output tree cannot be walked while it is being built).
   */
  function sanitizeRichDescriptionNode(sourceNode, targetParent, insideSvg = false) {
    if (!sourceNode || !targetParent) return;

    if (sourceNode.nodeType === Node.TEXT_NODE) {
      targetParent.appendChild(document.createTextNode(sourceNode.textContent || ''));
      return;
    }

    if (sourceNode.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const tagName = sourceNode.tagName ? sourceNode.tagName.toLowerCase() : '';

    if (!tagName || DESCRIPTION_RICH_SKIP_TAGS.has(tagName)) {
      return;
    }

    if (!DESCRIPTION_RICH_ALLOWED_TAGS.has(tagName)) {
      Array.from(sourceNode.childNodes || []).forEach((childNode) => {
        sanitizeRichDescriptionNode(childNode, targetParent, insideSvg);
      });
      return;
    }

    // An HTML <title> is not content; only an SVG title (inside a rebuilt <svg>) is kept.
    if (tagName === 'title' && !insideSvg) {
      return;
    }

    const outputTagName = DESCRIPTION_RICH_TAG_REMAP.get(tagName) || tagName;
    const nextParent = isSvgDescriptionTag(tagName)
      ? document.createElementNS(SVG_NAMESPACE, outputTagName)
      : document.createElement(outputTagName);

    // Anchors keep only a safe href, title and target; every other element goes through the allowlists.
    if (tagName === 'a') {
      const href = sourceNode.getAttribute('href');

      if (isSafeDescriptionLink(href)) {
        nextParent.setAttribute('href', href.trim());
      }

      const title = sourceNode.getAttribute('title');
      if (title) {
        nextParent.setAttribute('title', title);
      }

      const target = sourceNode.getAttribute('target');
      if (target === '_blank') {
        nextParent.setAttribute('target', '_blank');
        nextParent.setAttribute('rel', 'noopener noreferrer');
      }
    } else {
      copyRichDescriptionAttributes(sourceNode, nextParent, tagName);
    }

    Array.from(sourceNode.childNodes || []).forEach((childNode) => {
      sanitizeRichDescriptionNode(childNode, nextParent, insideSvg || tagName === 'svg');
    });

    // Keep media-only containers such as <p><img></p>; drop elements that rebuilt to nothing.
    if (
      tagName === 'br' ||
      tagName === 'img' ||
      isSvgDescriptionTag(tagName) ||
      nextParent.textContent.trim() ||
      nextParent.childElementCount > 0
    ) {
      targetParent.appendChild(nextParent);
    }
  }

  function buildSanitizedRichDescriptionFragment(sourceTemplate) {
    const sourceFragment = parseLiteralRichDescriptionFragment(parseDescriptionSourceFragment(sourceTemplate));
    const sanitizedFragment = document.createDocumentFragment();

    Array.from(sourceFragment.childNodes || []).forEach((childNode) => {
      sanitizeRichDescriptionNode(childNode, sanitizedFragment);
    });

    return sanitizedFragment;
  }

  /**
   * Sanitizes each description source once per page load. Callers that insert the result must clone
   * it; the preview builder only reads from it.
   */
  function getSanitizedRichDescriptionFragment(sourceTemplate) {
    if (!sourceTemplate) {
      return document.createDocumentFragment();
    }

    let sanitizedFragment = sanitizedRichDescriptionFragments.get(sourceTemplate);

    if (!sanitizedFragment) {
      sanitizedFragment = buildSanitizedRichDescriptionFragment(sourceTemplate);
      sanitizedRichDescriptionFragments.set(sourceTemplate, sanitizedFragment);
    }

    return sanitizedFragment;
  }

  // ---------------------------------------------------------------------------
  // Entry points.
  // ---------------------------------------------------------------------------

  /** Replaces the strip_html fallback in `.product-card__description-full-text` with the rich copy. */
  function initRichDescriptionFullText(root = document) {
    const richSourceTemplates = root.querySelectorAll('[data-description-preview-source][data-description-rich-source]');

    richSourceTemplates.forEach((sourceTemplate) => {
      const descriptionWrapper = sourceTemplate.closest('[data-description-expandable]');
      const fullText = descriptionWrapper ? descriptionWrapper.querySelector('.product-card__description-full-text') : null;

      if (!fullText || fullText.dataset.descriptionRichInitialized === 'true') return;

      const richFragment = getSanitizedRichDescriptionFragment(sourceTemplate).cloneNode(true);

      if (!richFragment.childNodes.length) {
        fullText.dataset.descriptionRichInitialized = 'true';
        return;
      }

      fullText.textContent = '';
      fullText.appendChild(richFragment);
      fullText.dataset.descriptionRichInitialized = 'true';
    });
  }

  /** Fills every `[data-description-preview-output]` with a word-limited text preview. */
  function initDescriptionPreviews(root = document) {
    const descriptions = root.querySelectorAll('[data-description-expandable]');

    descriptions.forEach((descriptionWrapper) => {
      if (descriptionWrapper.dataset.descriptionPreviewInitialized === 'true') return;

      const sourceTemplate = descriptionWrapper.querySelector('[data-description-preview-source]');
      const previewText = descriptionWrapper.querySelector('[data-description-preview-text]');

      if (!sourceTemplate || !previewText) {
        descriptionWrapper.dataset.descriptionPreviewInitialized = 'true';
        return;
      }

      const previewOutputs = previewText.querySelectorAll('[data-description-preview-output]');

      if (!previewOutputs.length) {
        descriptionWrapper.dataset.descriptionPreviewInitialized = 'true';
        return;
      }

      previewOutputs.forEach((previewOutput) => {
        const outputMode = previewOutput.dataset.descriptionPreviewOutput || 'full';
        const wordLimit = getDescriptionPreviewWordLimit(previewText, outputMode);
        const sourceFragment = getSanitizedRichDescriptionFragment(sourceTemplate);
        const previewFragment = buildDescriptionPreviewFragment(sourceFragment, wordLimit);

        if (!previewFragment.textContent.trim()) {
          return;
        }

        previewOutput.textContent = '';
        previewOutput.appendChild(previewFragment);
      });

      descriptionWrapper.dataset.descriptionPreviewInitialized = 'true';
    });
  }

  window.DescriptionSanitizer = {
    initRichDescriptionFullText,
    initDescriptionPreviews
  };
})();
